import { useEffect, useRef, useState } from "react";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";

import UserAvatar from "../management/UserAvatar";
import {
    primaryHoverBorder,
    primarySurface,
    primaryTint,
} from "../../surfaceTints";
import { ROOT_HUB } from "./desktopAccess";

// Rows share the file navigator's folder row: a 60px rounded tile with a 40px
// icon, filled green for the hub that is open and grey for the hubs above it.
const rowSx = (state) => (theme) => ({
    height: 60,
    my: 0.625,
    px: 1.25,
    gap: 1.5,
    borderRadius: "10px",
    border: "1px solid",
    borderColor: state === "current" ? "primary.main" : "divider",
    color: state === "current" ? "common.white" : "text.primary",
    backgroundColor:
        state === "current"
            ? theme.palette.mode === "dark"
                ? "rgb(0, 128, 53)"
                : "rgb(0, 149, 61)"
            : state === "ancestor"
              ? theme.palette.mode === "dark"
                  ? "#29342d"
                  : "#e1e5e2"
              : theme.palette.mode === "dark"
                ? theme.palette.background.subtle
                : "rgb(245, 245, 245)",
    transition:
        "background-color 220ms cubic-bezier(0.2, 0, 0, 1), border-color 220ms ease, transform 150ms ease",
    "&:hover": {
        backgroundColor:
            state === "current"
                ? "rgb(0, 137, 56)"
                : theme.palette.mode === "dark"
                  ? "#313e35"
                  : "#d9dfda",
        transform: "translateY(-1px)",
    },
    "& .MuiAvatar-root": {
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: "8px",
        color: state === "current" ? "common.white" : "text.secondary",
        backgroundColor:
            state === "current"
                ? "rgba(255, 255, 255, 0.16)"
                : theme.palette.action.hover,
    },
    "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
        "&:hover": { transform: "none" },
    },
});

const labelSx = {
    flex: 1,
    minWidth: 0,
    fontSize: 16,
    lineHeight: 1.25,
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    overflow: "hidden",
};

function HubRow({ hub, state, selectedBelow, onOpen }) {
    return (
        <ListItemButton
            onClick={onOpen}
            aria-current={state === "current" ? "true" : undefined}
            sx={rowSx(state)}
        >
            <UserAvatar userOrHub={hub} path="hubs" variant="rounded" />
            <Typography component="span" sx={labelSx}>
                {hub.name}
            </Typography>
            {selectedBelow > 0 && (
                <Chip
                    size="small"
                    label={selectedBelow + " ticked"}
                    sx={{
                        flexShrink: 0,
                        height: 22,
                        color: "inherit",
                        backgroundColor:
                            state === "current"
                                ? "rgba(255, 255, 255, 0.2)"
                                : "action.selected",
                    }}
                />
            )}
            <ArrowForwardIosIcon sx={{ fontSize: 14, flexShrink: 0, opacity: 0.7 }} />
        </ListItemButton>
    );
}

// Groups are the things that can be ticked, so they don't share the hubs'
// grey folder tile: a white card with a green group icon and its own checkbox,
// which turns green once the group is ticked
const groupRowSx = (checked) => (theme) => ({
    display: "flex",
    alignItems: "center",
    height: 60,
    my: 0.625,
    pl: 0.5,
    borderRadius: "10px",
    border: "1px solid",
    borderColor: checked ? "primary.main" : "divider",
    backgroundColor: checked ? primarySurface(theme) : "background.paper",
    transition: "background-color 220ms ease, border-color 220ms ease",
    "&:hover": {
        borderColor: checked ? "primary.main" : primaryHoverBorder(theme),
    },
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
});

export function GroupRow({ group, checked, onToggle, onOpen, detail }) {
    const members = (group.users || []).length;

    return (
        <Box sx={groupRowSx(checked)}>
            <Checkbox
                checked={checked}
                onChange={onToggle}
                inputProps={{ "aria-label": "Select " + group.name }}
            />
            <ListItemButton
                onClick={onOpen}
                aria-label={"View the members of " + group.name}
                sx={{
                    height: "100%",
                    minWidth: 0,
                    pl: 0.5,
                    pr: 1.25,
                    gap: 1.5,
                    borderRadius: "0 9px 9px 0",
                    "&:hover": { backgroundColor: "transparent" },
                    "&:hover .group-members": { textDecoration: "underline" },
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "8px",
                        color: "primary.main",
                        backgroundColor: primaryTint,
                    }}
                >
                    <GroupsOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: 16, lineHeight: 1.25 }}>
                        {group.name}
                    </Typography>
                    <Typography noWrap variant="body2" sx={{ color: "text.secondary" }}>
                        {detail && detail + " · "}
                        <Box
                            component="span"
                            className="group-members"
                            sx={{ color: "primary.main", fontWeight: 600 }}
                        >
                            {members === 1 ? "1 member" : members + " members"}
                        </Box>
                    </Typography>
                </Box>
            </ListItemButton>
        </Box>
    );
}

function SectionLabel({ icon, label, count }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 0.75,
                pt: 1.25,
                pb: 0.25,
                color: "text.secondary",
            }}
        >
            {icon}
            <Typography
                variant="caption"
                sx={{ flex: 1, fontWeight: 800, letterSpacing: "0.025em" }}
            >
                {label}
            </Typography>
            <Typography variant="caption">{count}</Typography>
        </Box>
    );
}

/**
 * Browses hubs as folders, like the file navigator: one column per open hub,
 * each listing its sub-hubs and then the groups that belong to it. Ticking a
 * group adds it to the selection, which survives moving around the tree;
 * clicking the rest of the group's row opens its member list.
 */
export default function HubGroupNavigator({
    tree,
    selectedGroups,
    onToggleGroup,
    onViewGroup,
    height,
}) {
    // The hubs currently open, from the top level down
    const [path, setPath] = useState([]);
    const scrollRef = useRef(null);

    // A hub can disappear under us (deleted elsewhere); close it and anything below
    const validPath = [];
    for (const key of path) {
        if (!tree.hubByKey.has(key)) break;
        validPath.push(key);
    }

    const columns = [ROOT_HUB, ...validPath];

    useEffect(() => {
        const node = scrollRef.current;
        if (node && typeof node.scrollTo === "function") {
            node.scrollTo({ left: node.scrollWidth, behavior: "smooth" });
        }
    }, [path.length]);

    function openHub(columnIndex, hubKey) {
        setPath((previous) => [...previous.slice(0, columnIndex), hubKey]);
    }

    function selectedCountBelow(hubKey) {
        return tree
            .groupKeysBelow(hubKey)
            .filter((key) => selectedGroups.has(key)).length;
    }

    return (
        <Box
            ref={scrollRef}
            sx={{
                display: "flex",
                height: height,
                overflowX: "auto",
                overflowY: "hidden",
                overscrollBehaviorX: "contain",
                scrollSnapType: { xs: "x mandatory", sm: "none" },
            }}
        >
            {columns.map((hubKey, columnIndex) => {
                const openChild = columns[columnIndex + 1];
                const hubs = tree.childHubs(hubKey);
                const groups = tree.groupsIn(hubKey);
                const title =
                    hubKey === ROOT_HUB ? "All hubs" : tree.hubByKey.get(hubKey).name;

                return (
                    <Box
                        key={hubKey}
                        data-testid={"hub-column-" + hubKey}
                        sx={{
                            flex: {
                                xs: "0 0 100%",
                                sm: "0 0 50%",
                                md: "0 0 33.333%",
                                lg: "0 0 25%",
                                xl: "0 0 20%",
                            },
                            minWidth: 0,
                            height: "100%",
                            overflowY: "auto",
                            scrollSnapAlign: "start",
                            borderRight: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Typography
                            sx={{
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                                px: 1.5,
                                py: 1.25,
                                fontFamily: "DraigRegular",
                                fontSize: 18,
                                lineHeight: 1.15,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                backgroundColor: "background.paper",
                            }}
                        >
                            {title}
                        </Typography>
                        <Box sx={{ px: 0.75, pb: 1 }}>
                            {hubs.length > 0 && (
                                <SectionLabel
                                    icon={<AccountTreeOutlinedIcon sx={{ fontSize: 17 }} />}
                                    label="Hubs"
                                    count={hubs.length}
                                />
                            )}
                            {hubs.map((hub) => (
                                <HubRow
                                    key={hub.key}
                                    hub={hub}
                                    state={
                                        hub.key === openChild
                                            ? columnIndex + 2 === columns.length
                                                ? "current"
                                                : "ancestor"
                                            : "idle"
                                    }
                                    selectedBelow={selectedCountBelow(hub.key)}
                                    onOpen={() => openHub(columnIndex, hub.key)}
                                />
                            ))}
                            {groups.length > 0 && (
                                <SectionLabel
                                    icon={<GroupsOutlinedIcon sx={{ fontSize: 17 }} />}
                                    label="Groups"
                                    count={groups.length}
                                />
                            )}
                            {groups.map((group) => (
                                <GroupRow
                                    key={group.key}
                                    group={group}
                                    checked={selectedGroups.has(group.key)}
                                    onToggle={() => onToggleGroup(group.key)}
                                    onOpen={() => onViewGroup(group)}
                                />
                            ))}
                            {hubs.length === 0 && groups.length === 0 && (
                                <Typography
                                    variant="body2"
                                    sx={{ p: 1.5, color: "text.secondary" }}
                                >
                                    No sub-hubs or groups here.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}
